import { fromJS } from 'immutable';
import { createSelector } from 'reselect';
import { DateTime } from 'luxon';
import { ListDailyBattery } from '../../api/Performance';

// ACTIONS TYPES
export const FETCH_DAILY_BATTERY_SUCCESS = 'customer-portal/performance/FETCH_DAILY_BATTERY_SUCCESS';
export const FETCH_DAILY_BATTERY_FAILURE = 'customer-portal/performance/FETCH_DAILY_BATTERY_FAILURE';
export const FETCHING_DAILY_BATTERY = 'customer-portal/performance/FETCHING_DAILY_BATTERY';

// ACTION CREATORS
export const fetchDailyBatterySuccess = battery => ({
  type: FETCH_DAILY_BATTERY_SUCCESS,
  payload: battery,
});

export const fetchDailyBatteryFailure = (err, startDate, endDate) => ({
  type: FETCH_DAILY_BATTERY_FAILURE,
  error: err,
  startDate,
  endDate,
});

export const fetchingDailyBattery = () => ({
  type: FETCHING_DAILY_BATTERY,
});

const aggregateDailyBatteries = data => {
  const dailyDict = {};
  if (!data || !data.site || !data.site.batteryDaily) {
    return null;
  }
  data.site.batteryDaily.edges.forEach(battery => {
    battery.node.performanceData.forEach(row => {
      const readingDate = DateTime.fromISO(row.readingEndTimeLocal, { zone: data.site.tzName }).endOf('day').toISO();
      // eslint-disable-next-line no-restricted-globals
      const energyDischargedKwh = !isNaN(row.energyDischargedKwh) ? parseFloat(row.energyDischargedKwh) : 0;
      if (readingDate in dailyDict) {
        dailyDict[readingDate].energyDischargedKwh += energyDischargedKwh;
      } else {
        dailyDict[readingDate] = {
          energyDischargedKwh,
          readingEndTimeLocal: readingDate,
        };
      }
    });
  });
  return Object.keys(dailyDict).map(key => dailyDict[key]);
};

// THUNKS
export const fetchDailyBattery = (prospectId, startDate, endDate) => async dispatch => {
  dispatch(fetchingDailyBattery());
  try {
    const { data } = await ListDailyBattery(prospectId, startDate, endDate);
    const aggregatedBattery = aggregateDailyBatteries(data);
    dispatch(
      fetchDailyBatterySuccess({
        data: aggregatedBattery,
        prospectId,
        startDate,
        endDate,
      }),
    );
  } catch (error) {
    dispatch(fetchDailyBatteryFailure(error.message, startDate, endDate));
  }
};

export const shouldFetchDailyBattery = (dailyBatteryState, prospectId, startDate, endDate) => {
  if (dailyBatteryState.get('loading')) {
    return false;
  }

  const readings = dailyBatteryState.get('readings');
  const existing = readings.filter(
    reading =>
      reading.prospectId === prospectId.toString() &&
      DateTime.local(reading.startDate).hasSame(DateTime.local(startDate), 'day') &&
      DateTime.local(reading.endDate).hasSame(DateTime.local(endDate), 'day'),
  );

  return existing.size === 0;
};

export const fetchDailyBatteryIfNeeded = (prospectId, startDate, endDate) => (dispatch, getState) => {
  if (!shouldFetchDailyBattery(getState().hourlyBattery, prospectId, startDate, endDate)) {
    return null;
  }
  return dispatch(fetchDailyBattery(prospectId, startDate, endDate));
};

// SELECTORS
const dailyBatterySelector = state => state.dailyBattery;

export const getDailyBattery = (prospectId, startDate, endDate) =>
  createSelector([dailyBatterySelector], dailyBattery => {
    const allReadings = dailyBattery.get('readings');
    const readings = allReadings.filter(
      reading =>
        reading.prospectId === prospectId.toString() &&
        DateTime.fromISO(reading.startDate).hasSame(DateTime.fromISO(startDate), 'minute') &&
        DateTime.fromISO(reading.endDate).hasSame(DateTime.fromISO(endDate), 'minute'),
    );
    if (readings.size > 0) {
      return readings.first().data;
    }
    return null;
  });

export const getDailyBatteryLoading = createSelector([dailyBatterySelector], dailyBattery => dailyBattery.get('loading') || !dailyBattery.get('loaded'));

// REDUCER
export const initialState = fromJS({
  loaded: false,
  loading: false,
  error: null,
  readings: [],
});

export default (state = initialState, action) => {
  switch (action.type) {
    case FETCH_DAILY_BATTERY_SUCCESS:
      return state
        .set('loaded', true)
        .set('loading', false)
        .updateIn(['readings'], arr => arr.push(action.payload));
    case FETCH_DAILY_BATTERY_FAILURE:
      return state.set('loading', false).set('loaded', true).set('error', action.error);
    case FETCHING_DAILY_BATTERY:
      return state.set('loading', true).set('error', null);
    default:
      return state;
  }
};
